import { Injectable } from '@angular/core';
import { ConnectionService } from '@services/connection-service';
import { AppConfig } from '../../app/app.config';
import { RecentlyViewedData, RecentlyViewedConfig, RecentlyViewedLocation, RecentlyViewedEnum } from '@shared/types/recently-viewed.types';

@Injectable({
  providedIn: 'root'
})
export class RecentlyViewedProductsService {

  constructor(
    private readonly connectionService: ConnectionService,
    private readonly appConfig: AppConfig
  ) {}

  async getRecentlyViewedData(
    location: RecentlyViewedLocation,
    isFullPage: boolean = false,
    recentlyViewedExperiment: any = null
  ): Promise<RecentlyViewedData | null> {

    const currentUser = this.connectionService.getActingUser();
    const isUnpaidUser = currentUser?.get('orderState') !== this.appConfig.Shared.User.OrderState.DELIVERED;
    if (isUnpaidUser || !recentlyViewedExperiment) {
      return null;
    }
    const experimentConfig = this.getExperimentConfig(recentlyViewedExperiment);

    try {
      const days = experimentConfig?.numberOfDays ?? RecentlyViewedEnum.Defaults.NUMBER_OF_DAYS;
      const limit = isFullPage ? undefined : this.getProductLimit(location, experimentConfig);

      const products = await this.connectionService.fetchRecentlyViewedProducts(days, limit);
      const recentlyViewedProducts = products || [];

      const shouldShowComponent = this.shouldRenderComponent(
        isFullPage,
        experimentConfig,
        recentlyViewedProducts.length
      );

      return {
        items: recentlyViewedProducts,
        shouldShowComponent,
        config: experimentConfig
      };

    } catch (error) {
      return {
        items: [],
        shouldShowComponent: false,
        config: null,
      };
    }
  }

  getExperimentConfig(experiment: any): RecentlyViewedConfig | null {
    if (!experiment || Object.keys(experiment).length === 0) {
      return null;
    }

    return {
      numberOfDays: experiment.variant?.numberOfDays ?? RecentlyViewedEnum.Defaults.NUMBER_OF_DAYS,
      minProductCount: experiment.variant?.minProductCount ?? RecentlyViewedEnum.Defaults.MIN_PRODUCT_COUNT,
      countOfProductsHP: experiment.variant?.countOfProductsHP ?? RecentlyViewedEnum.Defaults.COUNT_OF_PRODUCTS_HP,
      countOfProductsSP: experiment.variant?.countOfProductsSP ?? RecentlyViewedEnum.Defaults.COUNT_OF_PRODUCTS_SP,
      tags: experiment.variant?.tags ?? {}
    };
  }

  private getProductLimit(location: string, experimentConfig: RecentlyViewedConfig | null): number {
    if (!experimentConfig) return RecentlyViewedEnum.Defaults.FALLBACK_PRODUCT_LIMIT;

    switch (location) {
      case RecentlyViewedEnum.Location.HOME:
        return experimentConfig.countOfProductsHP;
      case RecentlyViewedEnum.Location.SHOP:
        return experimentConfig.countOfProductsSP;
      default:
        return experimentConfig.countOfProductsHP;
    }
  }

  private shouldRenderComponent(
    isFullPage: boolean,
    experimentConfig: RecentlyViewedConfig | null,
    productCount: number
  ): boolean {
    if (isFullPage) {
      return true;
    }
    if (experimentConfig) {
      return productCount >= experimentConfig.minProductCount;
    }
    return false;
  }

  getProductTag(recentView: any, experimentConfig: RecentlyViewedConfig | null): { text: string; show: boolean, type: string } {
    const previouslyBoughtTag = this.getPreviouslyBoughtTag(recentView);
    if (previouslyBoughtTag) return previouslyBoughtTag;

    const bestSellerTag = this.getBestSellerTag(recentView);
    if (bestSellerTag) return bestSellerTag;

    const customTag = this.getCustomTag(recentView, experimentConfig);
    if (customTag) return customTag;

    return { text: '', show: false, type: RecentlyViewedEnum.TagType.CUSTOM };
  }

  private getPreviouslyBoughtTag(recentView: any): { text: string; show: boolean, type: string } | null {
    if (recentView?.isPreviouslyBought) {
      return { text: 'Previously Bought', show: true, type: RecentlyViewedEnum.TagType.PREVIOUSLY_BOUGHT };
    }
    return null;
  }

  private getBestSellerTag(recentView: any): { text: string; show: boolean, type: string } | null {
    if (recentView?.product?.isBestSeller) {
      return { text: 'Best Seller', show: true, type: RecentlyViewedEnum.TagType.BEST_SELLER };
    }
    return null;
  }

  private getCustomTag(recentView: any, experimentConfig: RecentlyViewedConfig | null): { text: string; show: boolean, type: string } | null {
    const customTagText = this.getCustomTagText(recentView, experimentConfig);
    if (!customTagText) return null;

    const tagType = this.determineTagType(customTagText);
    return { text: customTagText, show: true, type: tagType };
  }

  private getCustomTagText(recentView: any, experimentConfig: RecentlyViewedConfig | null): string | null {
    if (!experimentConfig?.tags || !recentView?.product?.objectId) return null;
    return experimentConfig.tags[recentView.product.objectId] || null;
  }

  private determineTagType(tagText: string): string {
    const lowerCaseTag = tagText.toLowerCase();

    if (lowerCaseTag === 'previously bought') {
      return RecentlyViewedEnum.TagType.PREVIOUSLY_BOUGHT;
    }

    if (lowerCaseTag === 'best seller') {
      return RecentlyViewedEnum.TagType.BEST_SELLER;
    }

    return RecentlyViewedEnum.TagType.CUSTOM;
  }
}